import { useParams, useNavigate } from "react-router-dom";
import { MessageCircle, ArrowLeft, ShoppingCart } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "../lib/Urls";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../contexts/CartContext";
import { toast } from "../hooks/use-toast";
import { getStoredToken, getStoredUserId } from "../lib/authStorage";

const ItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [openImage, setOpenImage] = useState(false);
  const { toggleCartItem, isInCart } = useCart();

  const currentUserId = getStoredUserId();
  const token = getStoredToken();
  const isSignedIn = Boolean(token && currentUserId);

  // ================= ITEM (FAST CACHE) =================
  const { data: item } = useQuery({
    queryKey: ["item", id],
    queryFn: async () => {
      const res = await api.get(`/api/category-items/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 min cache = instant revisit
    cacheTime: 1000 * 60 * 30,
  });

  // ================= SELLER (DEPENDENT BUT CACHED) =================
  const { data: seller } = useQuery({
    queryKey: ["seller", item?.userId],
    queryFn: async () => {
      const res = await api.get(`/api/users/${item.userId}`);
      return res.data;
    },
    enabled: !!item?.userId,
    staleTime: 1000 * 60 * 30,
  });

  // ================= LOADING (ONLY FIRST TIME) =================
  if (!item) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto p-6 animate-pulse">
          <div className="h-6 w-1/3 bg-gray-300 rounded mb-4"></div>
          <div className="h-64 w-full bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  const getRemainingTime = (timestamp, availPeriod) => {
    const posted = new Date(timestamp).getTime();

    const expiry =
      posted + Number(availPeriod) * 60 * 60 * 1000;

    const remaining = expiry - Date.now();

    if (remaining <= 0) {
      return "Expired";
    }

    const totalSeconds = Math.floor(remaining / 1000);

    const days = Math.floor(totalSeconds / (60 * 60 * 24));

    const hours = Math.floor(
      (totalSeconds % (60 * 60 * 24)) / (60 * 60)
    );

    const minutes = Math.floor(
      (totalSeconds % (60 * 60)) / 60
    );

    const seconds = totalSeconds % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m left`;
    }

    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    }

    if (minutes > 0) {
      return `${minutes}m ${seconds}s left`;
    }

    return `${seconds}s left`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">

        {/* BACK */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* IMAGE */}
          <img
            onClick={() => setOpenImage(true)}
            src={item.imageUrl}
            alt={item.itemName}
            className="w-full h-[400px] object-cover rounded-xl"
          />

          {/* DETAILS */}
          <div className="space-y-4">

            <h1 className="text-3xl font-bold">{item.itemName}</h1>

            <p className="text-muted-foreground">
              {item.description}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xl font-semibold text-primary">
                {item.currency} {Number(item.amount).toLocaleString()}
              </p>

              <button
                type="button"
                onClick={() => {
                  const added = toggleCartItem(item);
                  toast({
                    title: added ? "Added to cart" : "Removed from cart",
                    description: item.itemName,
                    duration: 2500,
                  });
                }}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  isInCart(item.itemId)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-secondary text-secondary-foreground hover:bg-muted"
                }`}
              >
                <ShoppingCart className="h-4 w-4" />
                {isInCart(item.itemId) ? "In cart" : "Add to cart"}
              </button>
            </div>

            <div className="text-sm">
              Country: {item.locCountry || seller?.country || "Not specified"}
            </div>

            <div className="text-sm">
              City: {item.locCity || seller?.city || "Not specified"}
            </div>

            <div className="text-sm">
              Type: <span className="font-medium text-primary capitalize">{item.type}</span>
            </div>

            <div className="text-sm">
              Pickup Point: <span className="font-medium">{item.store}</span>
            </div>

            <div className="text-sm">
              Available Upto:{" "}
              <span className="font-medium text-primary">
                {getRemainingTime(item.timestamp, item.availPeriod)}
              </span>

            </div>


            {/*<div className="text-sm">
              Phone: {seller?.phone || "Not specified"}
            </div>

            <div className="text-sm">
              WhatsApp: {seller?.whatsapp || "Not specified"}
            </div> */}


            <div className="text-sm">
              Posted By: <span className="font-medium text-primary">{seller?.username || "Not specified"}</span>
            </div>

          </div>
        </div>
      </div>

      {/* CHAT BUTTON - ALWAYS VISIBLE */}
      <button
        type="button"
        aria-label="Chat with sender"
        onClick={() => {
          if (!isSignedIn) {
            toast({
              title: "Sign in required",
              description: "Please sign in first to chat with the sender.",
              duration: 3000,
            });

            navigate("/signin", {
              state: {
                from: `/item/${item.itemId}`,
              },
            });
            return;
          }

          if (currentUserId === Number(item.userId)) {
            toast({
              title: "Your listing",
              description: "You cannot start a chat with yourself.",
              duration: 2500,
            });
            return;
          }

          navigate(`/chat/${item.itemId}/${item.userId}`, {
            state: {
              sellerId: item.userId,
              itemId: item.itemId,
              itemName: item.itemName,
            },
          });
        }}
        className="fixed bottom-5 right-4 z-40 flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-xl transition hover:scale-105 hover:shadow-2xl sm:bottom-6 sm:right-6"
      >
        <MessageCircle className="h-5 w-5" />
        <span>Chat with seller</span>
      </button>

      {openImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setOpenImage(false)}
        >
          {/* CLOSE BUTTON */}
          <button
            onClick={() => setOpenImage(false)}
            className="absolute top-6 right-6 text-white text-3xl font-bold hover:scale-110 transition"
          >
            &times;
          </button>

          <img
            src={item.imageUrl}
            alt={item.itemName}
            className="max-w-[90vw] max-h-[90vh] rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ItemDetails;